'use client';

/**
 * Organiser sign up, step 1 of 2: the account.
 *
 * Email first, then name and password, then the emailed code ("Confirm your
 * email"). Google or Apple skip all of that, because they already prove the
 * email address, and land straight on step 2 (the application). The name
 * comes from the provider; if Apple withholds it, Clerk sends them to
 * /sign-up/continue and this screen asks for just the name.
 *
 * On Clerk's headless sign-up resource (useSignUp), for the same reasons as
 * sign in: the steps and the errors are ours, and no Clerk UI renders. There
 * is no passkey anywhere: organisers sign in with a password, Google or Apple.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth, useClerk, useSignUp } from '@clerk/nextjs';
import {
  authErrorMessage,
  clerkErrorCode,
  emailFieldError,
  isCompleteCode,
  passwordRuleMet,
  sanitiseCode,
} from '@/lib/auth/auth-flow';
import {
  AthleteNote,
  AuthFooter,
  AuthHeader,
  AuthIconTile,
  AuthIntro,
  AuthMain,
  AuthPage,
  AuthTitle,
  CodeInput,
  EmailChip,
  FieldLabel,
  FieldMessage,
  MailIcon,
  OrDivider,
  OrganiserArtPanel,
  PasswordInput,
  PrimaryButton,
  ProviderButtons,
  SignUpProgress,
  TextAction,
  TextInput,
  PasswordRule,
} from './parts';
import { clearAuthHandoff, recallEmail, rememberEmail } from './session';

type Step = 'email' | 'details' | 'code' | 'continue';

const APPLICATION_PATH = '/sign-up/organisation';

/** `preview` renders one step with no Clerk state, for the visual QA pages only. */
export type SignUpPreview = { step: Step; email: string };

export function OrganiserSignUp({ preview }: { preview?: SignUpPreview } = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const clerk = useClerk();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded, signUp, setActive } = useSignUp();

  const [step, setStep] = useState<Step>(preview?.step ?? 'email');
  const [email, setEmail] = useState(preview?.email ?? '');
  const [emailErr, setEmailErr] = useState<ReactNode>(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [detailsErr, setDetailsErr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [providerErr, setProviderErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const carried = recallEmail();
    if (carried) setEmail(carried);
  }, []);

  // Back from Google or Apple with a field still missing (usually the name).
  useEffect(() => {
    if (!isLoaded || !signUp) return;
    if (pathname?.endsWith('/continue') && signUp.status === 'missing_requirements') {
      setFirst(signUp.firstName ?? '');
      setLast(signUp.lastName ?? '');
      setEmail(signUp.emailAddress ?? '');
      setStep('continue');
    }
  }, [isLoaded, signUp, pathname]);

  // Already signed in: step 1 is done, go to step 2 (which routes approved
  // organisers on to the dashboard).
  useEffect(() => {
    if (!preview && authLoaded && isSignedIn) router.replace(APPLICATION_PATH);
  }, [preview, authLoaded, isSignedIn, router]);

  const toSignIn = () => {
    rememberEmail(email);
    router.push('/sign-in');
  };

  /** Part-way in, the header offers Sign out: everything typed is cleared. */
  const startOver = () => {
    clearAuthHandoff();
    setStep('email');
    setEmail('');
    setFirst('');
    setLast('');
    setPassword('');
    setCode('');
    setEmailErr(null);
    setPwErr(null);
    setDetailsErr(null);
    setCodeErr(null);
    setResent(false);
    if (clerk.session) void clerk.signOut({ redirectUrl: '/sign-up' });
    if (pathname !== '/sign-up') router.replace('/sign-up');
  };

  const finish = async (createdSessionId: string | null) => {
    await setActive?.({ session: createdSessionId });
    router.replace(APPLICATION_PATH);
  };

  const submitEmail = async () => {
    const problem = emailFieldError(email);
    if (problem) return setEmailErr(problem);
    if (!isLoaded || !signUp) return;
    setBusy('email');
    setEmailErr(null);
    try {
      // Creating the attempt now surfaces "already have an account" on this
      // step rather than after they have typed a name and password.
      await signUp.create({ emailAddress: email.trim() });
      setStep('details');
    } catch (err) {
      const { code: c, status } = clerkErrorCode(err);
      if (c === 'form_identifier_exists' || c === 'identifier_already_exists') {
        setEmailErr(
          <>
            There&rsquo;s already an account with that email.{' '}
            <TextAction onClick={toSignIn}>Sign in instead</TextAction>
          </>,
        );
      } else {
        setEmailErr(authErrorMessage(c, 'signup-email', status));
      }
    } finally {
      setBusy(null);
    }
  };

  const canCreate = !!(first.trim() && last.trim() && passwordRuleMet(password));
  const missingDetails = [first.trim(), last.trim(), passwordRuleMet(password)].filter((x) => !x).length;

  const submitDetails = async () => {
    if (!canCreate || !signUp) return;
    setBusy('details');
    setPwErr(null);
    setDetailsErr(null);
    try {
      await signUp.update({ firstName: first.trim(), lastName: last.trim(), password });
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setCode('');
      setCodeErr(null);
      setResent(false);
      setStep('code');
    } catch (err) {
      const { code: c, status } = clerkErrorCode(err);
      if (c && c.startsWith('form_password')) setPwErr(authErrorMessage(c, 'new-password', status));
      else setDetailsErr(authErrorMessage(c, 'signup-email', status));
    } finally {
      setBusy(null);
    }
  };

  const submitCode = async () => {
    if (!signUp || !isCompleteCode(code)) return;
    setBusy('code');
    setCodeErr(null);
    try {
      const res = await signUp.attemptEmailAddressVerification({ code });
      if (res.status === 'complete') {
        await finish(res.createdSessionId);
        return;
      }
      setCodeErr("We couldn't finish creating your account. Start again with your email.");
    } catch (err) {
      const { code: c, status } = clerkErrorCode(err);
      setCodeErr(authErrorMessage(c, 'code', status));
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    if (!signUp) return;
    setCodeErr(null);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setResent(true);
    } catch (err) {
      const { code: c, status } = clerkErrorCode(err);
      setCodeErr(authErrorMessage(c, 'code', status));
    }
  };

  const submitContinue = async () => {
    if (!signUp || !first.trim() || !last.trim()) return;
    setBusy('continue');
    setDetailsErr(null);
    try {
      const res = await signUp.update({ firstName: first.trim(), lastName: last.trim() });
      if (res.status === 'complete') {
        await finish(res.createdSessionId);
        return;
      }
      setDetailsErr("We need a little more to finish. Start again with your email.");
    } catch (err) {
      const { code: c, status } = clerkErrorCode(err);
      setDetailsErr(authErrorMessage(c, 'signup-email', status));
    } finally {
      setBusy(null);
    }
  };

  const startProvider = async (provider: 'google' | 'apple') => {
    if (!signUp) return;
    setBusy(provider);
    setProviderErr(null);
    try {
      await signUp.authenticateWithRedirect({
        strategy: provider === 'google' ? 'oauth_google' : 'oauth_apple',
        redirectUrl: '/sign-up/sso-callback',
        redirectUrlComplete: APPLICATION_PATH,
        continueSignUp: true,
      });
    } catch (err) {
      const { code: c, status } = clerkErrorCode(err);
      setProviderErr(authErrorMessage(c, 'provider', status));
      setBusy(null);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setCode('');
    setCodeErr(null);
  };

  const partWay = step === 'code';
  const header = partWay ? (
    <AuthHeader prompt={email.trim()} action={{ label: 'Sign out', onClick: startOver }} />
  ) : (
    <AuthHeader prompt="Already have an account?" action={{ label: 'Sign in', onClick: toSignIn }} />
  );

  return (
    <AuthPage>
      {header}
      {/* The picture stays beside every step (email, account, code), as on the first. */}
      <AuthMain panel={<OrganiserArtPanel />}>
        {step === 'email' && (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitEmail();
            }}
          >
            <AuthTitle large>From first entry to final result.</AuthTitle>
            <AuthIntro>
              Sell entries, run check-in on the day and get paid, all in one place. Free to set up. You only pay when you sell a ticket.
            </AuthIntro>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 28 }}>
              <ProviderButtons
                onGoogle={() => startProvider('google')}
                onApple={() => startProvider('apple')}
                busy={busy === 'google' || busy === 'apple' ? busy : null}
              />
            </div>
            {providerErr && <FieldMessage tone="error">{providerErr}</FieldMessage>}

            <OrDivider />

            <FieldLabel htmlFor="signup-email">Email</FieldLabel>
            <TextInput
              id="signup-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              invalid={!!emailErr}
              aria-invalid={!!emailErr || undefined}
              aria-describedby={emailErr ? 'signup-email-err' : undefined}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailErr(null);
              }}
            />
            {emailErr && (
              <FieldMessage id="signup-email-err" tone="error">
                {emailErr}
              </FieldMessage>
            )}
            {/* Clerk's bot check mounts here when it needs to. */}
            <div id="clerk-captcha" style={{ marginTop: 12 }} />
            <PrimaryButton loading={busy === 'email'} marginTop={4}>
              Continue
            </PrimaryButton>
            <p style={{ margin: '16px 0 0', fontSize: 12.5, color: 'var(--fvd-ink-muted)', lineHeight: 1.6, textWrap: 'pretty' }}>
              By continuing you agree to the{' '}
              <a href="/terms" className="fvd-link" style={{ color: 'var(--fvd-ink)' }}>
                Terms
              </a>{' '}
              and{' '}
              <a href="/privacy" className="fvd-link" style={{ color: 'var(--fvd-ink)' }}>
                Privacy Policy
              </a>
              .
            </p>

            <AthleteNote actionLabel="Join myfitvent" />
          </form>
        )}

        {(step === 'details' || step === 'continue') && (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void (step === 'continue' ? submitContinue() : submitDetails());
            }}
          >
            <SignUpProgress step={1} />
            {step === 'details' && <EmailChip email={email.trim()} onClick={backToEmail} />}
            <AuthTitle gap={step === 'details' ? 20 : 0}>Set up your account</AuthTitle>
            {step === 'continue' && <AuthIntro>Just your name, as you&rsquo;d like your team to see it.</AuthIntro>}

            <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2" style={{ marginTop: 26 }}>
              <div style={{ minWidth: 0 }}>
                <FieldLabel htmlFor="signup-first">First name</FieldLabel>
                <TextInput id="signup-first" autoComplete="given-name" value={first} onChange={(e) => setFirst(e.target.value)} />
              </div>
              <div style={{ minWidth: 0 }}>
                <FieldLabel htmlFor="signup-last">Last name</FieldLabel>
                <TextInput id="signup-last" autoComplete="family-name" value={last} onChange={(e) => setLast(e.target.value)} />
              </div>
            </div>

            {step === 'details' && (
              <div style={{ marginTop: 20 }}>
                <input type="email" autoComplete="username" value={email.trim()} readOnly hidden />
                <FieldLabel htmlFor="signup-password">Password</FieldLabel>
                <PasswordInput
                  id="signup-password"
                  value={password}
                  onChange={(v) => {
                    setPassword(v);
                    setPwErr(null);
                  }}
                  autoComplete="new-password"
                  invalid={!!pwErr}
                  shown={showPw}
                  onToggle={() => setShowPw((s) => !s)}
                />
                <PasswordRule met={passwordRuleMet(password)} />
                {pwErr && <FieldMessage tone="error">{pwErr}</FieldMessage>}
              </div>
            )}

            {step === 'details' && <div id="clerk-captcha" style={{ marginTop: 12 }} />}
            <PrimaryButton
              disabled={step === 'continue' ? !(first.trim() && last.trim()) : !canCreate}
              loading={busy === 'details' || busy === 'continue'}
              marginTop={step === 'details' ? 10 : 22}
            >
              {step === 'continue' ? 'Continue' : 'Create account'}
            </PrimaryButton>
            {step === 'details' && !canCreate && (
              <FieldMessage tone="hint">
                {missingDetails === 1 ? '1 thing left to fill in.' : `${missingDetails} things left to fill in.`}
              </FieldMessage>
            )}
            {detailsErr && <FieldMessage tone="error">{detailsErr}</FieldMessage>}
          </form>
        )}

        {step === 'code' && (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitCode();
            }}
          >
            <SignUpProgress step={1} />
            <AuthIconTile>
              <MailIcon />
            </AuthIconTile>
            <AuthTitle gap={18}>Confirm your email</AuthTitle>
            <AuthIntro>
              We sent a 6-digit code to <span style={{ color: 'var(--fvd-ink)', fontWeight: 500 }}>{email.trim()}</span>. It works for 10 minutes.
            </AuthIntro>
            <CodeInput
              value={code}
              onChange={(raw) => {
                setCode(sanitiseCode(raw));
                setCodeErr(null);
              }}
              invalid={!!codeErr}
              describedBy={codeErr ? 'signup-code-err' : undefined}
              autoFocus
            />
            {codeErr && (
              <FieldMessage id="signup-code-err" tone="error">
                {codeErr}
              </FieldMessage>
            )}
            <PrimaryButton disabled={!isCompleteCode(code)} loading={busy === 'code'}>
              Confirm email
            </PrimaryButton>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 18, fontSize: 13, color: 'var(--fvd-ink-secondary)' }}>
              {resent ? (
                <span>Sent again. Check spam if it&rsquo;s not there.</span>
              ) : (
                <>
                  <span>Didn&rsquo;t get it?</span>
                  <TextAction onClick={resend}>Send it again</TextAction>
                </>
              )}
              <span aria-hidden="true" style={{ color: 'var(--fvd-ink-disabled)' }}>
                ·
              </span>
              <TextAction onClick={backToEmail}>Use a different email</TextAction>
            </div>
          </form>
        )}
      </AuthMain>
      <AuthFooter />
    </AuthPage>
  );
}
